<template>
    <v-sheet
        color="primary"
        elevation="8"
        rounded="sm"
        class="trip-duration-panel pa-3"
        @click.stop
        @mousedown.stop
        @touchstart.stop
    >
        <v-text-field
            v-if="showDistanceInput"
            :model-value="localDistance"
            type="number"
            :label="$t('formFields.distanceKm')"
            density="compact"
            hide-details="auto"
            variant="outlined"
            color="yellow"
            base-color="yellow"
            class="trip-duration-panel__speed-input mb-2"
            @update:model-value="onDistanceInput"
        />
        <v-text-field
            :model-value="localSpeed"
            type="number"
            :label="$t('formFields.avgSpeedKmh')"
            density="compact"
            hide-details="auto"
            variant="outlined"
            color="yellow"
            base-color="yellow"
            class="trip-duration-panel__speed-input mb-2"
            :readonly="speedReadonly"
            @update:model-value="onSpeedInput"
        />

        <div
            v-if="showRouteLabel"
            class="text-body-2 font-weight-bold text-yellow mt-3 mb-2"
        >
            {{ routeLabel }}
        </div>

        <div class="trip-duration-panel__results">
            <div class="text-body-2 text-white mb-1">
                <span class="text-white">{{ $t("entries.totalDriveTime") }}:&nbsp;</span>
                <span :class="resultValueClass">{{ driveTimeDisplay }}</span>
            </div>
            <div class="text-body-2 text-white mb-1">
                <span class="text-white">{{ $t("entries.totalRestTime") }}:&nbsp;</span>
                <span :class="resultValueClass">{{ restTimeDisplay }}</span>
            </div>
            <div class="text-body-2 text-white mb-1">
                <span class="text-white">{{ $t("entries.totalTripTime") }}:&nbsp;</span>
                <span :class="resultValueClass">{{ tripTimeDisplay }}</span>
            </div>
        </div>

        <div class="text-caption text-white trip-duration-panel__explanation">
            {{ $t("entries.distanceCalculatorExplanation") }}
        </div>
    </v-sheet>
</template>

<script>
import { formatDurationCompact } from "@/utils";
import {
    computeEtaBreakdown,
    formatDurationCompactAllowZero,
} from "@/utils/etaCalculation";

export default {
    name: "TripDurationPanel",
    props: {
        entry: {
            type: Object,
            default: null,
        },
        distanceKm: {
            type: [String, Number],
            default: "",
        },
        showDistanceInput: {
            type: Boolean,
            default: false,
        },
        speedKmh: {
            type: [String, Number],
            default: "",
        },
        speedReadonly: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:speedKmh", "update:distanceKm"],
    computed: {
        localSpeed() {
            return this.speedKmh;
        },
        localDistance() {
            return this.distanceKm;
        },
        effectiveDistanceKm() {
            if (this.showDistanceInput) {
                return this.localDistance;
            }
            return this.entry?.distance_km;
        },
        showRouteLabel() {
            if (this.showDistanceInput || !this.entry) {
                return false;
            }
            const loadingCity = this.entry.loading_location?.cityName;
            const unloadingCity = this.entry.unloading_location?.cityName;
            return !!(loadingCity || unloadingCity);
        },
        resultValueClass() {
            return this.hasResults
                ? "text-yellow font-weight-bold"
                : "text-white";
        },
        breakdown() {
            const speed = Number(this.localSpeed);
            if (!Number.isFinite(speed) || speed <= 0) return null;
            return computeEtaBreakdown({
                distanceKm: this.effectiveDistanceKm,
                avgSpeedKmh: speed,
            });
        },
        hasResults() {
            return !!this.breakdown;
        },
        routeLabel() {
            const loadingCity = this.entry?.loading_location?.cityName || "—";
            const unloadingCity = this.entry?.unloading_location?.cityName || "—";
            return `${loadingCity} → ${unloadingCity}`;
        },
        driveTimeDisplay() {
            if (!this.breakdown) return "-";
            return formatDurationCompact(this.breakdown.drivingSeconds) ?? "-";
        },
        restTimeDisplay() {
            if (!this.breakdown) return "-";
            return formatDurationCompactAllowZero(
                this.breakdown.totalRestSeconds,
                formatDurationCompact
            );
        },
        tripTimeDisplay() {
            if (!this.breakdown) return "-";
            return formatDurationCompact(this.breakdown.totalTripSeconds) ?? "-";
        },
    },
    methods: {
        onSpeedInput(value) {
            if (!this.speedReadonly) {
                this.$emit("update:speedKmh", value);
            }
        },
        onDistanceInput(value) {
            this.$emit("update:distanceKm", value);
        },
    },
};
</script>

<style scoped>
.trip-duration-panel {
    width: 320px;
    max-width: min(320px, calc(100vw - 24px));
    border: 1px solid rgba(255, 235, 59, 0.35);
}

.trip-duration-panel__explanation {
    opacity: 0.9;
    line-height: 1.4;
}

.trip-duration-panel__speed-input :deep(input[type="number"]::-webkit-inner-spin-button),
.trip-duration-panel__speed-input :deep(input[type="number"]::-webkit-outer-spin-button) {
    -webkit-appearance: none;
    margin: 0;
}

.trip-duration-panel__speed-input :deep(input[type="number"]) {
    -moz-appearance: textfield;
    appearance: textfield;
}

.trip-duration-panel__results {
    margin-top: 16px;
}
</style>
